import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';

import { Button } from 'components/Button';
import { formatDateHandler } from 'utils/common';

/**
 * "Which size next time?" as a bottom sheet.
 *
 * Sizes are radio rows for the same reason the reject reasons are: a handful
 * of options on a phone are cheaper to read and tap than a select that shows
 * them one at a time, and each row is a full-width 44px target.
 *
 * The current preference is preselected, so the majority answer — "same as
 * last time" — is one tap on Save rather than a choice the employee has to
 * re-make. Keeping the same size is a real answer, not a no-op: the server
 * records it, and it is what separates a confirmed size from one carried
 * forward because nobody replied.
 */
const SizeChoiceSheet = ({ open, row, submitting, onCancel, onSubmit }) => {
  const [size, setSize] = useState(null);

  // Each uniform is its own question; reopening must never carry the previous
  // garment's answer across. Resetting on the preference too is safe because
  // saving closes the sheet before the refetch that changes it, so this can
  // never move a selection out from under someone mid-edit.
  useEffect(() => {
    if (open) setSize(row?.preferred_size ?? null);
  }, [open, row?.uniform_item, row?.preferred_size]);

  const sizes = row?.available_sizes || [];
  // Same format as every other date on this screen; see sizeWindow.on().
  const deadline = row?.change_deadline
    ? formatDateHandler(row.change_deadline) : null;
  const unchanged = size === row?.preferred_size;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={row ? `Your size — ${row.uniform_item_name}` : 'Your size'}
      className="myu-sheet"
      footer={[
        <Button
          key="cancel"
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onCancel}
          disabled={submitting}
          label="Cancel"
        />,
        <Button
          key="save"
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => onSubmit(row, size)}
          disabled={submitting || size === null}
          label={
            submitting ? 'Saving…'
              : unchanged ? 'Keep this size' : 'Save size'
          }
        />,
      ]}
    >
      {deadline && (
        <p className="myu-sheet-note">
          You can change this until {deadline}. After that your next
          uniform is ordered in whichever size is saved here.
        </p>
      )}

      <div className="myu-reasons" role="radiogroup" aria-label="Choose your size">
        {sizes.map(option => (
          <label className="myu-reason" key={option.id}>
            <input
              type="radio"
              name="myu-size-choice"
              value={option.id}
              checked={size === option.id}
              onChange={() => setSize(option.id)}
            />
            {option.name} ({option.code})
          </label>
        ))}
      </div>
    </Modal>
  );
};

export default SizeChoiceSheet;
